import React, { useState, useEffect } from 'react';
import './ResultsDashboard.css';
import { useUnsafeChatAdapter } from '@nlux/openai-react';
import { AiChat } from '@nlux/react';
import '@nlux/themes/nova.css';
import { utils, writeFileXLSX } from 'xlsx';

function ResultsDashboard({ navigate }) {
  const [results, setResults] = useState([]);

  const decodeKey = (key) => {
    return key?.replace(/_dot_/g, '.').replace(/_/g, ' ');
  };

  useEffect(() => {
    const fetchResults = async () => {
      const companyName = localStorage.getItem('companyName');

      if (!companyName) {
        alert('No company name found. Redirecting to login.');
        navigate('/intro');
        return;
      }

      try {
        const response = await fetch(`https://corexas-analysis-backend-i1q0.onrender.com/api/progress/${encodeURIComponent(companyName)}`, {
          method: 'GET',
          headers: {
            Authorization: localStorage.getItem('jwt'),
          },
        });

        if (response.ok) {
          const data = await response.json();
          const quizTypes = ['technology', 'strategy', 'innovation', 'sustainability'];
          let allResults = [];

          quizTypes.forEach(quizType => {
            if (data.quizProgress && data.quizProgress[quizType]) {
              const answers = data.quizProgress[quizType].answers || {};

              Object.entries(answers).forEach(([question, answerData]) => {
                allResults.push({
                  quizType,
                  question: decodeKey(question),
                  answer: answerData.value || 3.0,
                  target: answerData.targetValue || 'N/A',
                  bigSection: answerData.bigSection || 'N/A',
                  section: answerData.section || 'N/A',
                  questionNumber: answerData.questionNumber || 'N/A'
                });
              });
            }
          });

          // Sort results by quizType, bigSection, section, and questionNumber
          allResults.sort((a, b) => {
            if (a.quizType !== b.quizType) return a.quizType.localeCompare(b.quizType);
            if (a.bigSection !== b.bigSection) return a.bigSection.localeCompare(b.bigSection);
            if (a.section !== b.section) return a.section.localeCompare(b.section);
            return a.questionNumber - b.questionNumber;
          });

          setResults(allResults);
        } else {
          if (response.status === 404) {
            alert('Company not found. Redirecting to login.');
            navigate('/intro');
          } else if (response.status === 401) {
            alert('Unauthorized. Redirecting to login.');
            navigate('/');
          } else {
            alert('Failed to fetch results.');
          }
        }
      } catch (error) {
        console.error('Error fetching results:', error);
        alert('Error fetching results. Please try again later.');
      }
    };

    fetchResults();
  }, [navigate]);

  const adapter = useUnsafeChatAdapter({
    apiKey: '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',
    model: 'gpt-4o',
    dataTransferMode: 'batch',
    systemMessage: 'Given the company results below: \n' + JSON.stringify(results) + "\n Answer the questions. You are Corexas Consulting Group company assistant. "
  });

  const handleDownloadExcel = () => {
    // Create worksheet data
    const wsData = [
      ['Quiz Type', 'Big Section', 'Section', 'Question #', 'Question', 'Current Value', 'Target Value'], // Headers
      ...results.map(result => [
        result.quizType,
        result.bigSection,
        result.section,
        result.questionNumber,
        result.question,
        result.answer,
        result.target
      ])
    ];

    // Create worksheet
    const ws = utils.aoa_to_sheet(wsData);

    // Create workbook
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Results');

    // Generate Excel file
    const companyName = localStorage.getItem('companyName') || 'company';
    const fileName = `${companyName}-results.xlsx`;
    writeFileXLSX(wb, fileName);
  };

  return (
    <div className="results-dashboard">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Quiz Results Dashboard</h1>
        <button
          onClick={handleDownloadExcel}
          style={{
            padding: '10px 20px',
            backgroundColor: '#367db1',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#18558b'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#367db1'}
        >
          Download Excel
        </button>
      </div>
      <table className="results-table">
        <thead>
          <tr>
            <th>Quiz Type</th>
            <th>Big Section</th>
            <th>Section</th>
            <th>Question #</th>
            <th>Question</th>
            <th>Your Answer</th>
            <th>Target Value</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result, index) => (
            <tr key={index}>
              <td>{result.quizType}</td>
              <td>{result.bigSection}</td>
              <td>{result.section}</td>
              <td>{result.questionNumber}</td>
              <td>{result.question}</td>
              <td>{result.answer}</td>
              <td>{result.target}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <AiChat adapter={adapter} />
    </div>
  );
}

export default ResultsDashboard;
